import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Droplet, LogOut, Lock, ScrollText, Check, ChevronUp, Loader2, Home, Crown, ArrowRight, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAmbientAudio } from "@/context/AudioContext";
import { fetchReadings, fetchLunar } from "@/lib/api";
import { generateCovenantBlob, generateCovenantPdfBlob } from "@/lib/covenantCard";
import SigilField from "@/components/SigilField";
import DeckReveal from "@/components/DeckReveal";
import CelestialCouncil from "@/components/CelestialCouncil";
import MysteryEvents from "@/components/MysteryEvents";
import AscensionRitual from "@/components/AscensionRitual";
import { OATH_LINES, NDA, LEXICON, RELICS, MYSTERIES, IMAGES } from "@/data/society";

const ease = [0.22, 1, 0.36, 1];

const TIER_ORDER = ["The Novitiate", "The Adept", "The Matriarch"];

const AscensionSigil = ({ targetTier, memberTier, onAscended }) => {
  const { enterWithSigil } = useAuth();
  const [sigil, setSigil] = useState("");
  const [busy, setBusy] = useState(false);
  const attained = TIER_ORDER.indexOf(memberTier) >= TIER_ORDER.indexOf(targetTier);
  const testId = targetTier.replace(/\s+/g, "-").toLowerCase();

  if (attained) {
    return (
      <p
        className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#C5A059]/30 px-4 py-2 text-[0.6rem] uppercase tracking-[0.18em] text-[#C5A059]"
        data-testid={`ascension-attained-${testId}`}
      >
        <Check size={13} /> Attained
      </p>
    );
  }

  const ascend = async () => {
    if (!sigil.trim()) return toast.error("Speak your Sigil to ascend.");
    setBusy(true);
    try {
      const me = await enterWithSigil(sigil.trim());
      if (TIER_ORDER.indexOf(me.tier) > TIER_ORDER.indexOf(memberTier)) {
        onAscended && onAscended(me.tier);
      } else {
        toast.success(`You now stand as ${me.tier}.`);
      }
    } catch (e) {
      toast.error("The gate does not know this Sigil.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6" data-testid={`ascension-${testId}`}>
      <p className="overline mb-2 text-[0.55rem]">Enter your Sigil to ascend</p>
      <div className="flex max-w-sm gap-2">
        <input
          value={sigil}
          onChange={(e) => setSigil(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && ascend()}
          placeholder="XXXX-00-XXXXX"
          className="flex-1 rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-2.5 font-mono text-sm tracking-[0.12em] text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
          data-testid={`ascension-input-${testId}`}
        />
        <button
          onClick={ascend}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#C5A059] bg-[#C5A059] px-5 py-2.5 text-[0.6rem] font-medium uppercase tracking-[0.16em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          data-testid={`ascension-btn-${testId}`}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ChevronUp size={13} />}
          Ascend
        </button>
      </div>
    </div>
  );
};

const NAV = [
  { id: "codex", label: "The Codex" },
  { id: "celestial", label: "The Celestial Council" },
  { id: "relics", label: "The Relics" },
  { id: "chronicle", label: "The Chronicle" },
  { id: "mysteries", label: "The Mysteries" },
  { id: "inner", label: "The Inner Sanctum" },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "";
  }
};

/* ----------------------------- The Oath ------------------------------ */
const TheOath = ({ onSeal, sealing }) => {
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="relative min-h-screen w-full bg-[#040404]" data-testid="oath-flow">
      <SigilField />
      <div className="relative z-10 mx-auto max-w-2xl px-6 py-28">
        <div className="text-center">
          <p className="overline mb-5">Before You Pass</p>
          <h1 className="font-serif text-[clamp(2.4rem,7vw,4rem)] font-light leading-[1] tracking-tight text-white">
            The <span className="italic text-[#C5A059]">Oath</span>
          </h1>
          <p className="mt-5 text-sm font-light text-white/45">
            Read every line. What is entered here cannot be unspoken.
          </p>
        </div>

        <div className="mt-20 space-y-14">
          {OATH_LINES.map((line, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-25%" }}
              transition={{ duration: 1.1, ease }}
              className="text-center font-serif text-2xl font-light italic leading-relaxed text-white/75 md:text-3xl"
            >
              {line}
            </motion.p>
          ))}
        </div>

        {/* Non-Disclosure Agreement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease }}
          className="glass mt-28 rounded-3xl p-8 md:p-12"
          data-testid="oath-nda"
        >
          <div className="flex items-center gap-3 text-[#C5A059]">
            <Lock size={16} strokeWidth={1.5} />
            <p className="overline">{NDA.subtitle}</p>
          </div>
          <h2 className="mt-4 font-serif text-[clamp(1.8rem,5vw,3rem)] font-light tracking-tight text-white">
            The <span className="italic text-[#C5A059]">{NDA.title}</span>
          </h2>
          <p className="mt-6 text-sm font-light leading-relaxed text-white/55">{NDA.preamble}</p>

          <div className="mt-10 space-y-7 border-t border-[#C5A059]/10 pt-8">
            {NDA.clauses.map((c) => (
              <div key={c.heading} data-testid="nda-clause">
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-[#C5A059]">
                  {c.heading}
                </p>
                <p className="mt-2 text-sm font-light leading-relaxed text-white/60">{c.body}</p>
              </div>
            ))}
          </div>

          <label
            className="mt-10 flex cursor-pointer items-start gap-4 rounded-2xl border border-[#C5A059]/20 bg-black/30 p-5"
            data-testid="nda-agree-label"
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A059]"
              data-testid="nda-agree-checkbox"
            />
            <span className="text-sm font-light italic leading-relaxed text-white/70">
              {NDA.acknowledgement}
            </span>
          </label>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-16 flex flex-col items-center"
        >
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.25em] text-white/40">
            Seal your intention
          </p>
          <button
            onClick={onSeal}
            disabled={sealing || !agreed}
            className="group inline-flex items-center gap-3 rounded-full border border-[#7a2222] bg-[#5A1818] px-10 py-5 text-xs font-medium uppercase tracking-[0.26em] text-[#F3EADC] shadow-[0_0_40px_rgba(90,24,24,0.6)] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#6d1e1e] disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="oath-seal-btn"
          >
            <Droplet size={16} strokeWidth={1.5} className="transition-transform group-hover:scale-110" />
            {sealing ? "Sealing…" : "Seal in Blood"}
          </button>
          {!agreed && (
            <p className="mt-4 text-[0.6rem] uppercase tracking-[0.2em] text-white/30" data-testid="nda-agree-hint">
              Accept the Covenant of Silence to seal
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

/* --------------------------- The Portal ------------------------------ */
const Portal = ({ member, onLogout }) => {
  const navigate = useNavigate();
  const [deckOpen, setDeckOpen] = useState(false);
  const [readings, setReadings] = useState([]);
  const [lunarActive, setLunarActive] = useState(null);
  const [ascendedTier, setAscendedTier] = useState(null);
  const isMatriarch = member.tier === "The Matriarch";

  const loadReadings = async () => {
    try {
      setReadings(await fetchReadings());
    } catch (e) {}
  };

  useEffect(() => {
    loadReadings();
    fetchLunar()
      .then((s) => setLunarActive(s.active))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!lunarActive) return;
    const key = `sybil_lunar_seen_${lunarActive.date.slice(0, 10)}_${lunarActive.type}`;
    if (localStorage.getItem(key)) return;
    const label = lunarActive.type === "new" ? "New Moon" : "Full Moon";
    toast(`A ${label} window is open in ${lunarActive.sign}.`, {
      description: "Enter the Celestial Council for your moon ritual.",
    });
    localStorage.setItem(key, "1");
  }, [lunarActive]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.lenis) window.lenis.scrollTo(el, { offset: -20 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const petition = (name) =>
    toast.success(`Your petition for ${name} has been recorded. An Oracle will respond.`);

  const [covenantBusy, setCovenantBusy] = useState(false);
  const [covenantPdfBusy, setCovenantPdfBusy] = useState(false);
  const covenantMeta = () => ({
    name: member.name,
    date: new Date(member.created_at || Date.now()).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
  });
  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const slug = () => member.name.replace(/\s+/g, "-").toLowerCase();
  const downloadCovenant = async () => {
    setCovenantBusy(true);
    try {
      const blob = await generateCovenantBlob(covenantMeta());
      triggerDownload(blob, `covenant-of-silence-${slug()}.png`);
      toast.success("Your sealed Covenant is yours to keep.");
    } catch (e) {
      toast.error("The Covenant could not be drawn. Try again.");
    } finally {
      setCovenantBusy(false);
    }
  };
  const downloadCovenantPdf = async () => {
    setCovenantPdfBusy(true);
    try {
      const blob = await generateCovenantPdfBlob(covenantMeta());
      triggerDownload(blob, `covenant-of-silence-${slug()}.pdf`);
      toast.success("Your print-ready Covenant is prepared.");
    } catch (e) {
      toast.error("The Covenant could not be drawn. Try again.");
    } finally {
      setCovenantPdfBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-[#040404]" data-testid="sanctum-portal">
      <SigilField />

      {/* Left vertical nav */}
      <nav className="fixed left-0 top-0 z-40 hidden h-screen w-56 flex-col justify-center gap-6 border-r border-[#C5A059]/10 bg-[#040404]/70 px-8 backdrop-blur-xl lg:flex">
        <p className="overline mb-4 text-[0.6rem]">The Tiers</p>
        {NAV.map((n) => (
          <button
            key={n.id}
            onClick={() => scrollTo(n.id)}
            className="text-left font-serif text-xl font-light text-white/60 transition-colors duration-300 hover:text-[#C5A059]"
            data-testid={`sanctum-nav-${n.id}`}
          >
            <span className="flex items-center gap-2">
              {n.label}
              {n.id === "celestial" && lunarActive && (
                <span className="relative flex h-2 w-2" data-testid="lunar-badge" title="A moon window is open">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#C5A059] opacity-70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#C5A059]" />
                </span>
              )}
            </span>
          </button>
        ))}
        {isMatriarch && (
          <button
            onClick={() => navigate("/matriarch")}
            className="mt-2 inline-flex items-center gap-2 text-left font-serif text-xl font-light text-[#C5A059] transition-colors duration-300 hover:text-white"
            data-testid="sanctum-nav-matriarch"
          >
            <Crown size={16} strokeWidth={1.5} /> The Ecosystem
          </button>
        )}
      </nav>

      {/* Member badge */}
      <div className="fixed right-6 top-6 z-40 flex items-center gap-3 rounded-full border border-[#C5A059]/20 bg-[#040404]/70 px-4 py-2.5 backdrop-blur-xl">
        <button
          onClick={() => navigate("/")}
          className="text-white/40 transition-colors hover:text-[#C5A059]"
          data-testid="sanctum-home-btn"
          title="Return to the Veil"
        >
          <Home size={17} strokeWidth={1.5} />
        </button>
        <div className="h-5 w-px bg-[#C5A059]/20" />
        <div className="text-right">
          <p className="text-xs font-light text-white/80" data-testid="sanctum-member-name">
            {member.name}
          </p>
          <p className="text-[0.6rem] uppercase tracking-[0.2em] text-[#C5A059]">{member.tier}</p>
        </div>
        <button
          onClick={onLogout}
          className="text-white/40 transition-colors hover:text-[#C5A059]"
          data-testid="sanctum-logout-btn"
          title="Seal the veil behind you"
        >
          <LogOut size={17} strokeWidth={1.5} />
        </button>
      </div>

      <div className="lg:pl-56">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          {/* Codex / welcome */}
          <section id="codex" className="flex min-h-screen flex-col justify-center py-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease }}
            >
              <p className="overline mb-5">The Inner Cipher</p>
              <h1 className="font-serif text-[clamp(2.6rem,8vw,6rem)] font-light leading-[0.95] tracking-tight text-white">
                Welcome to the <br />
                <span className="italic text-[#C5A059]">Inner Sanctum</span>.
              </h1>
              <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/55">
                The outer world knows us by other names. Within these walls, we
                speak in the true tongue. Learn the cipher — it is the first relic
                you carry.
              </p>
            </motion.div>

            <div className="mt-16 max-w-2xl divide-y divide-[#C5A059]/10 border-y border-[#C5A059]/10">
              {LEXICON.map((l, i) => (
                <motion.div
                  key={l.coded}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.06, ease }}
                  className="flex items-center justify-between py-5"
                >
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/35 line-through">
                    {l.plain}
                  </span>
                  <span className="font-serif text-2xl font-light italic text-[#C5A059]">
                    {l.coded}
                  </span>
                </motion.div>
              ))}
            </div>
          </section>

          {/* The Celestial Council */}
          <CelestialCouncil />

          {/* Relics */}
          <section id="relics" className="py-28">
            <p className="overline mb-6">The Relics — Ritual Tools</p>
            <h2 className="mb-14 font-serif text-[clamp(2rem,5vw,4rem)] font-light tracking-tight text-white">
              Instruments of the <span className="italic text-[#C5A059]">Work</span>.
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {RELICS.map((r, i) => (
                <motion.article
                  key={r.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.9, delay: i * 0.08, ease }}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-[#C5A059]/15 bg-[#070707]"
                  data-testid={`relic-card-${r.id}`}
                >
                  <div
                    className={`relative aspect-square overflow-hidden ${
                      r.id === "scrying" ? "cursor-pointer" : ""
                    }`}
                    onClick={r.id === "scrying" ? () => setDeckOpen(true) : undefined}
                  >
                    <img
                      src={IMAGES[r.image]}
                      alt={r.name}
                      className="h-full w-full object-cover transition-transform [transition-duration:1200ms] group-hover:scale-110 group-hover:rotate-1"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-transparent to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full border border-[#C5A059]/40 bg-black/40 px-3 py-1 text-[0.55rem] uppercase tracking-[0.2em] text-[#C5A059]">
                      {r.tier}
                    </span>
                    {r.id === "scrying" && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                        <span className="rounded-full border border-[#C5A059]/60 bg-[#050505]/70 px-4 py-1.5 text-[0.6rem] uppercase tracking-[0.24em] text-[#C5A059] backdrop-blur-md">
                          ✦ Draw a card
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-white/30 line-through">
                      {r.was}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl font-light text-white">{r.name}</h3>
                    <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-white/50">
                      {r.lore}
                    </p>
                    <button
                      onClick={() => petition(r.name)}
                      className="mt-6 w-full rounded-full border border-[#C5A059]/40 py-3 text-[0.65rem] font-light uppercase tracking-[0.22em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505]"
                      data-testid={`relic-petition-${r.id}`}
                    >
                      Petition for Possession
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>

          {/* The Chronicle — reading history */}
          <section id="chronicle" className="py-28">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="overline mb-6">The Chronicle — Your Readings</p>
                <h2 className="font-serif text-[clamp(2rem,5vw,4rem)] font-light tracking-tight text-white">
                  Every card the deck has <span className="italic text-[#C5A059]">turned</span> for you.
                </h2>
              </div>
              <p className="max-w-xs text-sm font-light leading-relaxed text-white/45">
                Draws made while you are within the Sanctum are kept here — a private
                record of your archetypes across time.
              </p>
            </div>

            {readings.length === 0 ? (
              <div
                className="mt-14 flex flex-col items-center rounded-2xl border border-dashed border-[#C5A059]/25 py-20 text-center"
                data-testid="chronicle-empty"
              >
                <ScrollText size={30} strokeWidth={1} className="text-[#C5A059]/60" />
                <p className="mt-5 max-w-sm text-sm font-light leading-relaxed text-white/45">
                  Your chronicle is empty. Draw from the Governance Scrying above,
                  and each archetype will be inscribed here.
                </p>
              </div>
            ) : (
              <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
                {readings.map((r, i) => (
                  <motion.article
                    key={r.id || i}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-8%" }}
                    transition={{ duration: 0.7, delay: (i % 2) * 0.06, ease }}
                    className="flex gap-5 overflow-hidden rounded-2xl border border-[#C5A059]/15 bg-[#070707] p-4"
                    data-testid="reading-item"
                  >
                    <div className="relative h-28 w-24 flex-shrink-0 overflow-hidden rounded-xl">
                      <img src={r.image} alt={r.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-serif text-2xl font-light text-[#C5A059]">{r.name}</h3>
                        <span className="whitespace-nowrap font-mono text-[0.55rem] uppercase tracking-[0.16em] text-white/30">
                          {formatDate(r.created_at)}
                        </span>
                      </div>
                      <p className="overline mt-0.5 text-[0.5rem]">{r.title} · {r.element}</p>
                      <p className="mt-2 line-clamp-3 text-xs font-light italic leading-relaxed text-white/55">
                        “{r.statement}”
                      </p>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </section>

          {/* Mysteries / tiers */}
          <section id="mysteries" className="py-28">
            <p className="overline mb-6">The Mysteries — The Mystery School</p>
            <h2 className="mb-14 font-serif text-[clamp(2rem,5vw,4rem)] font-light tracking-tight text-white">
              Three tiers of <span className="italic text-[#C5A059]">ascension</span>.
            </h2>

            <MysteryEvents />

            <div className="space-y-6">
              {MYSTERIES.map((m, i) => (
                <motion.article
                  key={m.name}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-12%" }}
                  transition={{ duration: 0.9, delay: i * 0.06, ease }}
                  className="glass grid grid-cols-1 gap-6 overflow-hidden rounded-2xl md:grid-cols-[280px_1fr]"
                  data-testid={`mystery-tier-${i + 1}`}
                >
                  <div className="relative h-56 overflow-hidden md:h-full">
                    <img src={IMAGES[m.image]} alt={m.name} className="h-full w-full object-cover opacity-70" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0a]/60" />
                  </div>
                  <div className="p-8">
                    <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#C5A059]/80">
                      {m.tier}
                    </p>
                    <h3 className="mt-2 font-serif text-3xl font-light text-white">{m.name}</h3>
                    <p className="mt-1 font-serif text-lg font-light italic text-white/45">
                      {m.subtitle}
                    </p>
                    <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-white/55">
                      {m.body}
                    </p>
                    <AscensionSigil targetTier={m.name} memberTier={member.tier} onAscended={setAscendedTier} />
                  </div>
                </motion.article>
              ))}
            </div>
          </section>

          {/* Inner Sanctum status */}
          <section id="inner" className="flex min-h-[80vh] flex-col justify-center py-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease }}
              className="glass rounded-3xl p-10 md:p-16"
            >
              <div className="flex items-center gap-3 text-[#C5A059]">
                <Lock size={18} strokeWidth={1.5} />
                <p className="overline">The Inner Sanctum — Sealed Council</p>
              </div>
              <h2 className="mt-6 font-serif text-[clamp(2rem,5vw,4rem)] font-light leading-tight tracking-tight text-white">
                Your oath is <span className="italic text-[#C5A059]">sealed</span>,{" "}
                {member.name.split(" ")[0]}.
              </h2>
              <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/55">
                You now move within the encrypted circle. Capital is pooled here for
                matriarchal investment and quiet lobbying. The Network — our
                peer-to-peer channel — opens to you at the tier of{" "}
                <span className="text-[#C5A059]">The Matriarch</span>. Until then,
                tend the roots. Attend the Mysteries. Rise.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <span className="rounded-full border border-[#C5A059]/25 px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                  Standing · {member.tier}
                </span>
                <span className="rounded-full border border-[#5A1818]/60 bg-[#5A1818]/20 px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-[#e0b0b0]">
                  Oath · Sealed
                </span>
                <button
                  onClick={downloadCovenant}
                  disabled={covenantBusy}
                  className="group inline-flex items-center gap-2 rounded-full border border-[#C5A059]/40 px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-60"
                  data-testid="download-covenant-btn"
                >
                  {covenantBusy ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Download size={13} strokeWidth={1.75} />
                  )}
                  Covenant · Image
                </button>
                <button
                  onClick={downloadCovenantPdf}
                  disabled={covenantPdfBusy}
                  className="group inline-flex items-center gap-2 rounded-full border border-[#C5A059]/40 px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-60"
                  data-testid="download-covenant-pdf-btn"
                >
                  {covenantPdfBusy ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <ScrollText size={13} strokeWidth={1.75} />
                  )}
                  Covenant · PDF
                </button>
              </div>
              {isMatriarch && (
                <button
                  onClick={() => navigate("/matriarch")}
                  className="group mt-10 inline-flex items-center gap-2 rounded-full border border-[#C5A059] bg-[#C5A059] px-7 py-3.5 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5"
                  data-testid="enter-ecosystem-btn"
                >
                  <Crown size={14} strokeWidth={1.75} />
                  Enter the Matriarch Ecosystem
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              )}
            </motion.div>
          </section>
        </div>
      </div>

      <DeckReveal
        open={deckOpen}
        onClose={() => {
          setDeckOpen(false);
          loadReadings();
        }}
      />

      <AscensionRitual tier={ascendedTier} onClose={() => setAscendedTier(null)} />
    </div>
  );
};

/* --------------------------- Sanctum root ---------------------------- */
export const Sanctum = () => {
  const { member, seal, logout } = useAuth();
  const { start, setMode } = useAmbientAudio();
  const navigate = useNavigate();
  const [sealing, setSealing] = useState(false);

  const sealed = !!(member && member.oath_sealed);
  useEffect(() => {
    if (sealed) {
      (async () => {
        try {
          await start();
        } catch (e) {}
        setMode("sanctum");
      })();
    }
    return () => setMode("veil");
  }, [sealed, start, setMode]);

  if (member === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#040404]" data-testid="sanctum-loading">
        <p className="font-serif text-2xl font-light italic text-[#C5A059] animate-pulse">
          The gate opens…
        </p>
      </div>
    );
  }

  if (member === false) return <Navigate to="/gate" replace />;

  const handleSeal = async () => {
    setSealing(true);
    try {
      await seal();
      try {
        await start();
      } catch (e) {}
      setMode("sanctum");
      toast.success("Sealed. The Sanctum receives you.");
    } catch {
      toast.error("The seal did not hold. Try again.");
    } finally {
      setSealing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (!member.oath_sealed) return <TheOath onSeal={handleSeal} sealing={sealing} />;

  return <Portal member={member} onLogout={handleLogout} />;
};

export default Sanctum;
